import React, { useState, useEffect, useRef, createRef, RefObject } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { Table, Button, Ref } from 'semantic-ui-react'
import { BatchLookupValue } from '../../../redux/types/ui'
import { batchListMoveUp, batchListMoveDown } from '../../../redux/actions/ui'
import { BatchAliasAssignments } from '../../../redux/types/productAliases'
import { FinishWarningModal } from '../FinishWarningModal'
import { AppState } from '../../../redux/store'
import { CustomBatchListTable } from '../styles'
import { scrollParentToChild } from '../../../utils/scrollParentToChild'
import JSZip from 'jszip'
import { toast } from 'react-toastify'
import { makeBatchLookupCsvs } from './utils/makeBatchLookupCsvs'
import { BatchLookupListItem } from './BatchLookupListItem'
import { populateBatchProducts } from '../../../redux/actions/products'

interface Props {
  disableArrowKeys: boolean
  setPreSelectedId: (x: number | null | undefined) => void
}

const handleDownloadReport = async (
  batchLookupValues: BatchLookupValue[],
  batchAliasAssignments: BatchAliasAssignments
) => {
  const { all, needs_tlc, no_match, fix_category } = makeBatchLookupCsvs(
    batchLookupValues,
    batchAliasAssignments
  )

  const date = getDateString()
  const folderName = `${date}_alias_assignment_summary`
  const zip = new JSZip()
  const folder = zip.folder(folderName)

  if (!folder) {
    const message = `Could not create zip folder with name "${folderName}"`
    toast(message, { containerId: 'error' })
    throw Error(message)
  }

  all && folder.file(`${date}_summary.csv`, all)
  needs_tlc && folder.file(`${date}_needs-tlc.csv`, needs_tlc)
  no_match && folder.file(`${date}_no-match.csv`, no_match)
  fix_category && folder.file(`${date}_fix-category.csv`, fix_category)

  const blob = await zip.generateAsync({ type: 'blob' })

  const a = document.createElement('a')
  a.setAttribute('href', window.URL.createObjectURL(blob))
  a.setAttribute('download', `${folderName}.zip`)
  a.style.display = 'none'
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
}

const getDateString = () => {
  // https://stackoverflow.com/questions/1531093/how-do-i-get-the-current-date-in-javascript
  const today = new Date()
  const dd = String(today.getDate()).padStart(2, '0')
  const mm = String(today.getMonth() + 1).padStart(2, '0') //January is 0!
  const yyyy = today.getFullYear()
  return `${mm}_${dd}_${yyyy}`
}

const BatchLookupList = (props: Props) => {
  const dispatch = useDispatch()
  const [warningActive, setWarningActive] = useState(false)
  const [windowHeight, setWindowHeight] = useState(window.innerHeight)
  const [allowKeyAction, setAllowKeyAction] = useState(true)
  const { batchProgressError, batchLookupValues, currentBatchId } = useSelector(
    (state: AppState) => state.ui
  )
  const { batchAliasAssignments } = useSelector((state: AppState) => state.productAliases)

  // Create an array of refs to assign to each batch lookup item, we can then
  // scroll to the selected item using the ref from the array.
  const batchLookupValuesRefs = useRef<RefObject<HTMLElement>[]>([])
  batchLookupValues.forEach(
    (_el, i) => (batchLookupValuesRefs.current[i] = createRef<HTMLElement>())
  )
  const listContainerRef = useRef<HTMLDivElement>(null)

  // This effect will scroll the batch lookup list items to the selected item
  // if that item is not visible on the page.
  useEffect(() => {
    if (currentBatchId === undefined) return

    const index = parseInt(currentBatchId)
    const selected = batchLookupValuesRefs.current[index].current

    if (listContainerRef.current === null || selected === null) return

    scrollParentToChild(listContainerRef.current, selected)
  }, [currentBatchId, batchLookupValuesRefs])

  useEffect(() => {
    const windowResizeHandler = () => setWindowHeight(window.innerHeight)

    const keydownHandler = (e: KeyboardEvent) => {
      if (e.key === 'ArrowUp') {
        e.preventDefault()
        if (allowKeyAction && !props.disableArrowKeys) {
          dispatch(batchListMoveUp())
          setAllowKeyAction(false)
          setTimeout(() => {
            setAllowKeyAction(true)
          }, 10)
        }
      }

      if (e.key === 'ArrowDown') {
        e.preventDefault()
        if (allowKeyAction && !props.disableArrowKeys) {
          dispatch(batchListMoveDown())
          setAllowKeyAction(false)
          setTimeout(() => {
            setAllowKeyAction(true)
          }, 10)
        }
      }
    }

    window.addEventListener('keydown', keydownHandler)
    window.addEventListener('resize', windowResizeHandler)

    return () => {
      window.removeEventListener('keydown', keydownHandler)
      window.removeEventListener('resize', windowResizeHandler)
    }
  }, [dispatch, currentBatchId, allowKeyAction, batchLookupValues.length, props.disableArrowKeys])

  useEffect(() => {
    if (currentBatchId) {
      try {
        props.setPreSelectedId(batchLookupValues[parseInt(currentBatchId)].preSelectedId)
      } catch (e) {
        console.error({ error: e })
      }
    }
  }, [batchLookupValues, currentBatchId, props])

  return (
    <>
      <div
        style={{
          maxHeight: windowHeight - 230,
          overflow: 'auto',
          paddingRight: 2,
        }}
        ref={listContainerRef}
      >
        <CustomBatchListTable basic="very" padded selectable>
          <Table.Body>
            {batchLookupValues.map((batchLookupValue, index) => {
              const hadError = !!batchProgressError[index]
              const isActive = parseInt(currentBatchId || '') === index ? true : false
              const key = `${index}-${batchLookupValue.alias}`
              return (
                <Ref innerRef={batchLookupValuesRefs.current[index]} key={key}>
                  <BatchLookupListItem
                    batchLookupValue={batchLookupValue}
                    batchAliasAssignment={batchAliasAssignments[index]}
                    index={index}
                    hadError={hadError}
                    isActive={isActive}
                    updateSelectedItem={() =>
                      dispatch(populateBatchProducts({ batchId: index.toString() }))
                    }
                  />
                </Ref>
              )
            })}
          </Table.Body>
        </CustomBatchListTable>
      </div>
      <div style={{ paddingTop: 30, maxWidth: 150 }}>
        <Button
          fluid
          basic
          color="blue"
          onClick={() => handleDownloadReport(batchLookupValues, batchAliasAssignments)}
        >
          Download report
        </Button>
        <Button style={{ marginTop: 10 }} fluid basic onClick={() => setWarningActive(true)}>
          Finish
        </Button>
      </div>
      <FinishWarningModal
        warningActive={warningActive}
        setWarningActive={setWarningActive}
        handleDownloadReport={() => handleDownloadReport(batchLookupValues, batchAliasAssignments)}
        dispatch={dispatch}
      />
    </>
  )
}

export default BatchLookupList
